import { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  TextField, styled, IconButton, SvgIcon, Popover, Typography,
  InputAdornment, Box, Grid, Paper, useMediaQuery
} from '@mui/material';
import { ReactComponent as CalendarIcon } from "../../../icons-svg/icons/datePicker.svg";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import CloseIcon from '@mui/icons-material/Close';
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  format, addMonths, subMonths, startOfMonth, isAfter, isBefore, isSameDay,
  startOfWeek, subDays, addDays, isSameMonth, differenceInDays
} from "date-fns";
import "./custom-date-range-selector.scss";
import CustomButton from '../CustomButton/CustomButton';

const StyledTextField = styled(TextField)(() => ({
  maxWidth: "200px",
  "& .MuiOutlinedInput-root": {
    height: "34px",
    backgroundColor: "#121212",
    color: "#FFFFFF",
    borderRadius: "6px",

    "& fieldset": {
      borderColor: "#707070",
    },

    "&:hover fieldset": {
      borderColor: "#555",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#e6eff8",
    },
  },

  "& .MuiInputBase-input": {
    fontSize: "12px",
    padding: "10px 8px",
  },

  "& .MuiInputAdornment-root": {
    marginLeft: "2px",
  },

  "& .MuiIconButton-root": {
    padding: "4px"
  },

  "& .MuiSvgIcon-root": {
    color: "#FFFFFF",
    fontSize: "16px",
  },

  "& .MuiInputBase-input::placeholder": {
    color: "#9E9E9E",
    opacity: 1,
  },

  "& .MuiInputLabel-root": {
    color: "#C8C8C8",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#1976d2",
  },
}));

const PresetMenuItems = [
  { id: '7days', name: "Last 7 days" },
  { id: '30days', name: "Last 30 days" },
  { id: '60days', name: "Last 60 days" },
  { id: '90days', name: "Last 90 days" },
  { id: '12months', name: "Last 12 Months" },
  { id: 'customDates', name: "Custom Dates" }
];

const SummaryDateDisplay = ({ startDate, endDate, handleClose }) => (
  <div className="summary-date-display">
    <div className="summary-total-display">
      <p>
        <strong>Total Days </strong>
        <span>{(startDate && endDate) ? differenceInDays(addDays(endDate, 1), startDate) : 0}</span>
      </p>
      <p>Start Date <span>{startDate ? format(startDate, "MM/dd/yyyy") : "None"}</span></p>
    </div>
    <div className="start-end-display">
      <p>End Date <span>{endDate ? format(endDate, "MM/dd/yyyy") : "None"}</span></p>
    </div>
  </div>
);

const CustomDateRangeSelector = ({ onDateRangeSelect, resetTrigger }) => {
  const resetButtonRef = useRef(null);
  const applyButtonRef = useRef(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState({ startDate: null, endDate: null });

  // LEFT calendar = Current Month
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  // RIGHT calendar = currentMonth + 1
  const nextMonth = addMonths(currentMonth, 1);

  const [preset, setPreset] = useState("30days");
  const [minDate, setMinDate] = useState(subDays(new Date(), 29));

  const open = Boolean(anchorEl);

  useEffect(() => {
    setStartDate(subDays(new Date(), 29));
    setEndDate(new Date());
  }, []);

  useEffect(() => {
    if (resetTrigger) {
      // setStartDate(null);
      // setEndDate(null);
      setSelectedDateRange({ startDate: null, endDate: null });
    }
  }, [resetTrigger]);


  useEffect(() => {
    if (startDate && endDate && resetButtonRef?.current) {
      resetButtonRef?.current.focus();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (preset && preset !== "customDates" && applyButtonRef?.current) {
      applyButtonRef?.current.focus();
    }
  }, [preset]);

  const PRESET_CONFIG = {
    '7days': { sd: 6, ed: 0 },
    '30days': { sd: 29, ed: 0 },
    '60days': { sd: 59, ed: 0 },
    '90days': { sd: 89, ed: 0 },
    '12months': { sd: 364, ed: 0 },
    'customDates': { sd: null, ed: null }
  };

  const decideThePreset = useCallback((id) => {
    const cfg = PRESET_CONFIG[id];

    setStartDate(cfg.sd === null ? null : subDays(new Date(), cfg.sd));
    setEndDate(cfg.ed === null ? null : new Date());

    if (cfg.sd !== null) {
      const start = subDays(new Date(), cfg.sd);
      setCurrentMonth(startOfMonth(start));
    }

    setMinDate(cfg.sd === null ? subDays(new Date(), 364) : subDays(new Date(), cfg.sd));
  }, []);

  const handlePreset = (item) => {
    setPreset(item.id);
    decideThePreset(item.id);

    if (item.id === "customDates") {
      const today = new Date();
      setCurrentMonth(startOfMonth(today));

      setStartDate(null);
      setEndDate(null);

      setSelectedDateRange({ startDate: null, endDate: null });
      onDateRangeSelect?.(null);
    }
  };

  const handleDateClick = (date) => {
    if (!startDate || endDate) {
      setStartDate(date);
      setEndDate(null);
      return;
    }

    const [start, end] =
      isAfter(date, startDate)
        ? [startDate, date]
        : [date, startDate];

    setStartDate(start);
    setEndDate(end);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedDateRange({ startDate: null, endDate: null });
  };

  const handleApply = () => {
    setSelectedDateRange({ startDate: format(startDate, "MM/dd/yyyy"), endDate: format(endDate, "MM/dd/yyyy") });
    if (onDateRangeSelect) {
      onDateRangeSelect({
        startDate: format(startDate, "MM/dd/yyyy"),
        endDate: format(endDate, "MM/dd/yyyy")
      });
    }
    setAnchorEl(null);
  };

  const isDateInRange = (date) =>
    startDate && endDate && isAfter(date, startDate) && isBefore(date, endDate);

  const renderCalendar = (month, calendarIndex) => {
    const startGrid = startOfWeek(startOfMonth(month));
    const days = Array.from({ length: 42 }).map((_, i) => addDays(startGrid, i));

    return (
      <div>
        {/* Month Header */}
        <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 0.65, pb: 0.75 }}>
          <IconButton
            disabled={calendarIndex === "next"}
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className={calendarIndex === "next" ? "chevron-button-disabled" : "chevron-button"}
          >
            <ChevronLeft style={calendarIndex === "next" ? { color: "#363c46" } : { color: "#E5E7F0" }} />
          </IconButton>
          <p className="month-display-typograpgy">{format(month, "MMMM yyyy")}</p>
          <IconButton
            disabled={calendarIndex === "current"}
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className={calendarIndex === "current" ? "chevron-button-disabled" : "chevron-button"}
          >
            <ChevronRight style={calendarIndex === "current" ? { color: "#363c46" } : { color: "#E5E7F0" }} />
          </IconButton>
        </Grid>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", opacity: 0.6 }}>
          {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
            <Typography key={d} sx={{ textAlign: "center", fontSize: "0.75rem", color: "#E5E7F0" }}>
              {d}
            </Typography>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(30px, 0.5fr))", mt: 1 }}>
          {days.map((date) => {
            const inMonth = isSameMonth(date, month);
            const disabled = isBefore(date, minDate) || isAfter(date, new Date());
            const selected = isSameDay(date, startDate) || isSameDay(date, endDate);
            const inRange = isDateInRange(date);

            const isToday = isSameDay(date, new Date());
            const isStart = startDate && isSameDay(date, startDate);
            const isEnd = endDate && isSameDay(date, endDate);

            return (
              <Box role="grid"
                key={date.toISOString()}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 25, borderRadius: "0%" }}>
                {!inMonth ? (

                  <Typography
                    sx={{
                      opacity: 0,
                      visibility: "hidden",
                      pointerEvents: "none",
                      fontSize: "0.75rem",
                      height: "30px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {format(date, "d")}
                  </Typography>

                ) : (
                  <Paper key={date}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: isSameDay(date, startDate) ? "50% 0% 0% 50%" : isSameDay(date, endDate) ? "0% 50% 50% 0%" : "0px",
                      cursor: (disabled || selected) ? "not-allowed" : "pointer",
                      backgroundColor: (selected && endDate && startDate) ? "rgba(90, 124, 255, 0.18)" : "transparent",
                      boxShadow: "none",
                      width: "100%",
                      height: "30px"
                    }}>
                    <button
                      disabled={disabled}
                      onClick={() => !disabled && handleDateClick(date)}
                      tabIndex={disabled ? -1 : 0}
                      data-date-cell
                      aria-label={`${format(date, "MMMM d, yyyy")}`}
                      onKeyDown={(e) => {
                        const nodes = Array.from(document.querySelectorAll('[data-date-cell]'));
                        const currentIndex = nodes.findIndex((el) => el === e.currentTarget);
                        if (e.key === 'ArrowRight' && nodes[currentIndex + 1]) {
                          e.preventDefault();
                          (nodes[currentIndex + 1]).focus();
                        }
                        if (e.key === 'ArrowLeft' && nodes[currentIndex - 1]) {
                          e.preventDefault();
                          (nodes[currentIndex - 1]).focus();
                        }
                        if (e.key === 'ArrowDown' && nodes[currentIndex + 7]) {
                          e.preventDefault();
                          (nodes[currentIndex + 7]).focus();
                        }
                        if (e.key === 'ArrowUp' && nodes[currentIndex - 7]) {
                          e.preventDefault();
                          (nodes[currentIndex - 7]).focus();
                        }
                      }}
                      className={(disabled || !!inRange) && 'date-button'}
                      style={{
                        width: selected ? "85%" : inRange ? "100%" : "90%",
                        height: selected ? "85%" : "96%",
                        padding: "0rem 0rem",
                        display: "flex",
                        alignItems: "center",
                        border: isToday ? "2px solid #5A7CFF" : "none",
                        justifyContent: "center",
                        cursor: (disabled || inRange) ? "not-allowed" : "pointer",
                        borderRadius: selected
                          ? "50%"
                          : isStart
                            ? "50% 0 0 50%"
                            : isEnd
                              ? "0 50% 50% 0"
                              : inRange
                                ? 0
                                : "50%",
                        backgroundColor: selected
                          ? "#5A7CFF"
                          : inRange
                            ? "rgba(90, 124, 255, 0.18)"
                            : "transparent",

                        color: selected
                          ? "#FFFFFF"
                          : disabled
                            ? "#c7cbd6"
                            : "#E5E7F0",
                        textAlign: "center",
                        opacity: 1,
                        pointerEvents: disabled ? "none" : "auto",
                        boxShadow: "none",
                        fontSize: "0.75rem",
                      }}
                    >
                      {format(date, "d")}
                    </button>
                  </Paper>
                )}
              </Box>
            );
          })}
        </Box>
      </div>
    );
  };

  const displayValue = selectedDateRange.startDate && selectedDateRange.endDate
    ? `${selectedDateRange.startDate} - ${selectedDateRange.endDate}`
    : "";

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minWidth: 200 }}>
        <Typography
          sx={{
            mb: 1,
            fontSize: "12px",
            fontWeight: 600,
            color: "#9aa7b9"
          }}
        >
          Date Range
        </Typography>

        <StyledTextField
          placeholder="Select date range"
          value={displayValue}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <SvgIcon component={CalendarIcon} sx={{ color: "#FFFFFF" }} />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              cursor: "pointer"
            }
          }}
          inputProps={{
            readOnly: true,
            style: { cursor: "pointer" }
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              position: "fixed !important",
              left: "231px !important",
              borderRadius: "1rem",
              backgroundColor: "#3c4358",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
            }
          }
        }}
      >
        <Box className="date-range-container">
          <Box className="preset-menu-container">
            <p className="preset-header">Presets</p>
            {PresetMenuItems.map((p) => (
              <div key={p.id} style={{ margin: "0.4rem" }}>
                <CustomButton
                  label={p.name}
                  handleClick={() => handlePreset(p)}
                  buttonStyle={preset === p.id ? "preset-selected" : "preset-unselected"}
                />
              </div>
            ))}
          </Box>

          {/* Left Calendar */}
          <Box className="current-calendar-display">
            {renderCalendar(currentMonth, "current")}
            <SummaryDateDisplay startDate={startDate} endDate={endDate} />
          </Box>

          {/* Right Calendar */}
          <Box className="next-calendar-display">
            {renderCalendar(nextMonth, "next")}

            <div className="button-display">
              <CustomButton
                ref={resetButtonRef}
                label="Reset"
                handleClick={handleReset}
                buttonStyle="dateRange-filled"
                disabled={preset !== "customDates" || !startDate}
              />

              <CustomButton
                ref={applyButtonRef}
                label="Done"
                handleClick={handleApply}
                buttonStyle="dateRange-filled"
                disabled={!startDate || !endDate}
              />
            </div>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

SummaryDateDisplay.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  handleClose: PropTypes.func
};

export default CustomDateRangeSelector;